import { isValidPhoneNumber } from "react-phone-number-input";
import { getEmploymentFieldVisibility } from "web-apps/company/components/staff/components/shared/staffEmploymentFieldVisibility.logic";
import {
  getEmployeeNumberValidationError,
  getEmploymentDaysValidationError,
  getNameValidationError,
  getOptionalEmailValidationError,
  getOptionalPersonalIdentityNumberValidationError,
  getOptionalTextValidationError,
  getPhoneValidationError,
  getWorkPercentageValidationError,
  hasValidEmployeeNumber,
  isValidName,
  isValidOptionalEmail,
  isValidOptionalEmploymentDays,
  isValidOptionalPersonalIdentityNumber,
  isValidOptionalText,
  isValidRequiredText,
  isValidWorkPercentage,
  type StaffInputValidationError,
} from "web-apps/company/components/staff/components/shared/staffInputValidation.logic";
import type {
  EditingTab,
  EmploymentDetailsForm,
  PersonalDetailsForm,
} from "../profilePanel.contract";

interface CanSaveEmploymentEditParams {
  readonly editedEmploymentDetails: EmploymentDetailsForm;
  readonly editingTab: EditingTab;
  readonly isEndDateRequiredOnSave: boolean;
}

export interface PersonalFieldValidationErrors {
  readonly email: StaffInputValidationError | null;
  readonly firstName: StaffInputValidationError | null;
  readonly lastName: StaffInputValidationError | null;
  readonly personalIdentityNumber: StaffInputValidationError | null;
  readonly phoneNumber: StaffInputValidationError | null;
}

export interface EmploymentFieldValidationErrors {
  readonly agreement: StaffInputValidationError | null;
  readonly employeeNumber: StaffInputValidationError | null;
  readonly noticeDays: StaffInputValidationError | null;
  readonly role: StaffInputValidationError | null;
  readonly vacationDays: StaffInputValidationError | null;
  readonly workplace: StaffInputValidationError | null;
  readonly workPercentage: StaffInputValidationError | null;
}

function hasNonEmptyValue(value: string): boolean {
  return value.trim().length > 0;
}

export function canSavePersonalEdit(form: PersonalDetailsForm): boolean {
  return (
    isValidPhoneNumber(form.phoneNumber) &&
    isValidName(form.firstName) &&
    isValidName(form.lastName) &&
    isValidOptionalEmail(form.email) &&
    isValidOptionalPersonalIdentityNumber(form.personalIdentityNumber)
  );
}

export function getPersonalFieldValidationErrors(
  form: PersonalDetailsForm,
): PersonalFieldValidationErrors {
  return {
    email: getOptionalEmailValidationError(form.email),
    firstName: getNameValidationError({
      field: "firstName",
      value: form.firstName,
    }),
    lastName: getNameValidationError({
      field: "lastName",
      value: form.lastName,
    }),
    personalIdentityNumber: getOptionalPersonalIdentityNumberValidationError(
      form.personalIdentityNumber,
    ),
    phoneNumber: getPhoneValidationError(form.phoneNumber),
  };
}

export function getEmploymentFieldValidationErrors(
  form: EmploymentDetailsForm,
): EmploymentFieldValidationErrors {
  return {
    agreement: getOptionalTextValidationError({
      translationKey: "Staff.AddStaffPanelAgreementTooLong",
      value: form.agreement,
    }),
    employeeNumber: getEmployeeNumberValidationError(form.employeeNumber),
    noticeDays: getEmploymentDaysValidationError(form.noticeDays),
    role: getOptionalTextValidationError({
      translationKey: "Staff.AddStaffPanelRoleTooLong",
      value: form.role,
    }),
    vacationDays: getEmploymentDaysValidationError(form.vacationDays),
    workplace: getOptionalTextValidationError({
      translationKey: "Staff.AddStaffPanelWorkplaceTooLong",
      value: form.workplace,
    }),
    workPercentage: getWorkPercentageValidationError(form.workPercentage),
  };
}

function hasValidCoreEmploymentFields({
  form,
  isEndDateRequired,
}: {
  readonly form: EmploymentDetailsForm;
  readonly isEndDateRequired: boolean;
}): boolean {
  if (!hasNonEmptyValue(form.employmentType)) return false;
  if (
    !hasValidEmployeeNumber({ isRequired: true, value: form.employeeNumber })
  ) {
    return false;
  }
  if (!isValidRequiredText(form.role)) return false;
  if (form.startDate == null) return false;
  if (isEndDateRequired && form.endDate == null) return false;
  return form.endDate == null || form.endDate.isAfter(form.startDate);
}

function hasValidContractTerms(form: EmploymentDetailsForm): boolean {
  return (
    isValidOptionalText(form.agreement) &&
    isValidOptionalEmploymentDays(form.noticeDays) &&
    isValidOptionalEmploymentDays(form.vacationDays) &&
    isValidOptionalText(form.workplace)
  );
}

function hasValidVisibleEmploymentFields(form: EmploymentDetailsForm): boolean {
  const visibility = getEmploymentFieldVisibility(form.employmentType);
  if (
    visibility.showEmployeeCategory &&
    !hasNonEmptyValue(form.employeeCategory)
  ) {
    return false;
  }
  if (visibility.showContractTerms && !hasValidContractTerms(form))
    return false;
  if (
    visibility.showSalaryType &&
    form.salaryType === "Monthly" &&
    !isValidWorkPercentage(form.workPercentage)
  ) {
    return false;
  }
  return true;
}

export function canSaveEmploymentEdit({
  editedEmploymentDetails,
  editingTab,
  isEndDateRequiredOnSave,
}: CanSaveEmploymentEditParams): boolean {
  if (editingTab !== "employmentSalary") return true;
  if (
    !hasValidCoreEmploymentFields({
      form: editedEmploymentDetails,
      isEndDateRequired: isEndDateRequiredOnSave,
    })
  )
    return false;
  return hasValidVisibleEmploymentFields(editedEmploymentDetails);
}
